import React, { useMemo } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Pie, PieChart, Cell } from "recharts";
import styles from "./styles.module.scss";
import { IWidget } from "@pages/Dashboard/types/dashboard.types";
import classNames from "classnames";
import { useAdsAnaliticsStore } from "src/store/ads-analitics.store";
import { getProfitColor, formatName } from "./utils";
import { useIsMobile } from "src/utils/isMobile";
import { WidgetClass } from "@services/widget.service";

const areNumbersEqual = (a: number, b: number, epsilon = 0.0001): boolean =>
  Math.abs(a - b) < epsilon;

export interface MetricCardProps {
  metric: IWidget;
  metricColor?: string;
  goalValue?: number;
  maxValue?: number;
  showWhatIf?: boolean;
}

export const MetricCard = ({
  metric,
  metricColor,
  goalValue,
  maxValue,
  showWhatIf = false,
}: MetricCardProps) => {
  const widgetClass = useMemo(() => new WidgetClass(), []);
  const adSlides = useAdsAnaliticsStore((state) => state.adSlides);
  const isMobile = useIsMobile();

  const { standardValue, whatIfValue, hasWhatIfDiff, isWhatIfPositive } =
    useMemo(() => {
      const base = Number(metric.originalValue);
      const whatIf = metric.whatIfValue ?? base;
      const origDiff = !areNumbersEqual(whatIf, base);
      return {
        standardValue: base,
        whatIfValue: whatIf,
        hasWhatIfDiff: origDiff || showWhatIf,
        isWhatIfPositive: whatIf > base,
      };
    }, [metric.originalValue, metric.whatIfValue, showWhatIf]);

  const profit = useMemo(() => {
    const diff = metric.difference;
    if (typeof diff === "string") return diff.startsWith("+");
    return Number(diff) > 0;
  }, [metric.difference]);

  const isForecastNeeded = useMemo(
    () => Object.values(adSlides).some((v) => v !== 0),
    [adSlides],
  );

  const { metricValue, whatIfMetricValue, baseColor, whatIfColor } =
    useMemo(() => {
      let mVal = standardValue;
      let wVal = whatIfValue;
      const up = "#80C67A";
      const down = "#FE5858";
      let base = metricColor ?? (profit ? up : down);
      let wColor = isWhatIfPositive ? up : down;

      if (isForecastNeeded) {
        const total = Object.values(adSlides).reduce((a, v) => a + v, 0);
        mVal += (total * standardValue) / 100;
        wVal += (total * whatIfValue) / 100;
        base = getProfitColor(metric.key, mVal, maxValue ?? goalValue ?? mVal);

        const invert = /acos|cpc|cac/i.test(metric.key);
        if (invert) {
          wColor = isWhatIfPositive ? down : up;
        } else {
          wColor = isWhatIfPositive ? up : down;
        }
      }

      return {
        metricValue: mVal,
        whatIfMetricValue: wVal,
        baseColor: base,
        whatIfColor: wColor,
      };
    }, [
      standardValue,
      whatIfValue,
      profit,
      isWhatIfPositive,
      isForecastNeeded,
      adSlides,
      metricColor,
      metric.key,
      goalValue,
      maxValue,
    ]);

  const maxMetricValue = useMemo(() => {
    const ref = maxValue ?? goalValue;
    return ref != null
      ? Math.max(ref, metricValue, whatIfMetricValue) * 1.6
      : Math.max(metricValue, whatIfMetricValue) * 1.4;
  }, [maxValue, goalValue, metricValue, whatIfMetricValue]);

  const pieWidth = isMobile ? 165 : 190;
  const strokeWidth = isMobile ? 13 : 30;

  const { inner, outer, altInner, altOuter } = useMemo(() => {
    const extra = hasWhatIfDiff || (goalValue ?? 0) > 0;
    let innerRadius: number;
    let outerRadius: number;
    if (extra) {
      innerRadius = isMobile ? 65 : 80;
      outerRadius = isMobile ? 75 : 95;
    } else {
      innerRadius = pieWidth / 2 - strokeWidth;
      outerRadius = pieWidth / 2;
    }
    return {
      inner: innerRadius,
      outer: outerRadius,
      altInner: isMobile ? 55 : 65,
      altOuter: isMobile ? 65 : 80,
    };
  }, [hasWhatIfDiff, goalValue, isMobile, pieWidth, strokeWidth]);

  const renderChart = () => {
    const layers: React.ReactNode[] = [];

    layers.push(
      <Pie
        key="bg"
        data={[{ value: 100 }]}
        cx="50%"
        cy="90"
        startAngle={180}
        endAngle={0}
        innerRadius={inner}
        outerRadius={outer}
        dataKey="value"
        cornerRadius={32}
        fill="#2E2E2E"
        stroke="none"
      />,
    );

    layers.push(
      <Pie
        key="main"
        data={[{ value: metricValue }, { value: maxMetricValue }]}
        cx="50%"
        cy="90"
        startAngle={180}
        endAngle={0}
        innerRadius={inner}
        outerRadius={outer}
        cornerRadius={32}
        paddingAngle={0}
        stroke="none"
      >
        <Cell fill={baseColor} stroke="none" />
        <Cell fill="none" stroke="none" />
      </Pie>,
    );

    if (hasWhatIfDiff || (goalValue ?? 0) > 0) {
      layers.push(
        <Pie
          key="mid"
          data={[{ value: 100 }]}
          cx="50%"
          cy="90"
          startAngle={180}
          endAngle={0}
          innerRadius={altInner}
          outerRadius={altOuter}
          cornerRadius={32}
          paddingAngle={0}
          fill="#2E2E2E"
          stroke="none"
        />,
      );
    }

    if (hasWhatIfDiff) {
      layers.push(
        <Pie
          key="whatIf"
          data={[{ value: whatIfMetricValue }, { value: maxMetricValue }]}
          cx="50%"
          cy="90"
          startAngle={180}
          endAngle={0}
          innerRadius={altInner}
          outerRadius={altOuter}
          cornerRadius={32}
          paddingAngle={0}
          stroke="none"
        >
          <Cell fill={whatIfColor} stroke="none" />
          <Cell fill="none" stroke="none" />
        </Pie>,
      );
    }

    if ((goalValue ?? 0) > 0 && !hasWhatIfDiff) {
      layers.push(
        <Pie
          key="goal"
          data={[{ value: goalValue! }, { value: maxMetricValue }]}
          cx="50%"
          cy="90"
          startAngle={180}
          endAngle={0}
          innerRadius={altInner}
          outerRadius={altOuter}
          cornerRadius={32}
          paddingAngle={0}
          stroke="none"
        >
          <Cell fill={baseColor} stroke="none" />
          <Cell fill="none" stroke="none" />
        </Pie>,
      );
    }

    return (
      <PieChart
        width={pieWidth}
        height={pieWidth / 2}
        className={styles.metricPie}
      >
        {layers}
      </PieChart>
    );
  };

  const renderValues = () => {
    const parts: React.ReactNode[] = [];

    if (hasWhatIfDiff) {
      parts.push(
        <div key="whatIf" className={styles.whatIf}>
          <span className={styles.whatIfValue} style={{ color: whatIfColor }}>
            {widgetClass.formatValue(metric.key, whatIfValue)}
          </span>
          <h4>Current: {widgetClass.formatValue(metric.key, standardValue)}</h4>
        </div>,
      );
    }

    if ((goalValue ?? 0) > 0 && !hasWhatIfDiff) {
      parts.push(
        <div key="goalVal" className={styles.metricValue}>
          {widgetClass.formatValue(metric.key, goalValue!)}
        </div>,
      );
    }

    if (!hasWhatIfDiff) {
      parts.push(
        <h4
          key="current"
          className={classNames(
            styles.metricValue,
            (goalValue ?? 0) > 0 && styles.withGoals,
          )}
        >
          {(goalValue ?? 0) > 0 && "Current: "}
          {widgetClass.formatValue(metric.key, standardValue)}
        </h4>,
      );
    }

    if (metric.difference && metric.difference !== "0" && !hasWhatIfDiff) {
      parts.push(
        <div
          key="change"
          className={styles.metricChange}
          style={{ color: baseColor }}
        >
          {widgetClass.formatValue(metric.key, Number(metric.difference))}
          {profit ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        </div>,
      );
    }

    if (hasWhatIfDiff) {
      const diffPerc = (
        ((whatIfValue - standardValue) / standardValue) *
        100
      ).toFixed(1);
      parts.push(
        <div
          key="whatIfChange"
          className={styles.metricChange}
          style={{ color: whatIfColor }}
        >
          {(isWhatIfPositive ? "+" : "") + diffPerc + "%"}
          {isWhatIfPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        </div>,
      );
    }

    return parts;
  };

  return (
    <div
      className={classNames(
        styles.metricCard,
        !profit && styles.metricCardSmall,
      )}
    >
      <div className={styles.metricHeader}>{formatName(metric.name)}</div>
      <div className={styles.metricBody}>
        {renderChart()}
        <div className={styles.metricValuesBox}>{renderValues()}</div>
        <div className={styles.metricCircle}>
          <svg viewBox="0 0 100 52" version="1.1">
            <circle
              cx="50px"
              cy="50px"
              r="48"
              stroke="#C8A857"
              strokeWidth="2"
              strokeDasharray="0 10"
              strokeLinecap="round"
              fill="transparent"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};
